import React from 'react';
import { Box, Link, Typography } from '@mui/material';

/* Renders the small markdown subset the advisor writes (bold, italic, code, links, headings, lists)
 * as React nodes. No raw HTML is ever injected. */
const INLINE = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)\s]+\)|https?:\/\/[^\s)]+)/g;

function inline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of Array.from(text.matchAll(INLINE))) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    const key = `${keyBase}-${i++}`;
    if (tok.startsWith('**')) out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('[')) {
      const mm = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/.exec(tok);
      if (mm) out.push(<Link key={key} href={mm[2]} target="_blank" rel="noopener noreferrer">{mm[1]}</Link>);
    } else if (tok.startsWith('http')) out.push(<Link key={key} href={tok} target="_blank" rel="noopener noreferrer">{tok}</Link>);
    else out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const MiniMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const blocks: React.ReactNode[] = [];
  const lines = text.replace(/\r/g, '').split('\n');
  let list: { depth: number; text: string }[] = [];
  const flush = (k: number) => {
    if (!list.length) return;
    const items = list;
    list = [];
    blocks.push(
      <Box component="ul" key={`ul${k}`} sx={{ m: 0, mb: 1.25, pl: 2.5 }}>
        {items.map((it, j) => (
          <Box component="li" key={j} sx={{ ml: it.depth * 2.5, mb: 0.25 }}>{inline(it.text, `li${k}-${j}`)}</Box>
        ))}
      </Box>
    );
  };
  lines.forEach((raw, k) => {
    const line = raw.trimEnd();
    const li = /^(\s*)[-*]\s+(.*)$/.exec(line);
    if (li) { list.push({ depth: Math.min(2, Math.floor(li[1].length / 2)), text: li[2] }); return; }
    flush(k);
    if (!line.trim()) return;
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push(<Typography key={k} variant="subtitle1" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>{inline(h[2], `h${k}`)}</Typography>);
      return;
    }
    blocks.push(<Typography key={k} variant="body1" sx={{ mb: 1.25 }}>{inline(line, `p${k}`)}</Typography>);
  });
  flush(lines.length);
  return <Box sx={{ '& code': { fontFamily: 'var(--font-mono, monospace)', fontSize: '0.9em' }, '& > :last-child': { mb: 0 } }}>{blocks}</Box>;
};

export default MiniMarkdown;
